import { useEffect } from 'react'

const PIXEL_ID = '1302914075030097'
let pixelInitialized = false

declare global {
  interface Window {
    fbq: any
    _fbq: any
  }
}

/**
 * Inject the Meta Pixel script into the page head.
 * Uses the exact same snippet Facebook provides.
 */
export function initMetaPixel() {
  if (typeof window === 'undefined') return
  if (pixelInitialized) return
  pixelInitialized = true

  // 1. Create the fbq stub
  if (!window.fbq) {
    const n: any = (window.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
    })
    if (!window._fbq) window._fbq = n
    n.push = n
    n.loaded = true
    n.version = '2.0'
    n.queue = []
  }

  // 2. Inject the fbevents.js script
  const script = document.createElement('script')
  script.async = true
  script.src = 'https://connect.facebook.net/en_US/fbevents.js'
  const firstScript = document.getElementsByTagName('script')[0]
  if (firstScript && firstScript.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript)
  } else {
    document.head.appendChild(script)
  }

  // 3. Init pixel
  window.fbq('init', PIXEL_ID)

  // 4. Add noscript fallback image directly to body
  const noscriptImg = document.createElement('img')
  noscriptImg.height = 1
  noscriptImg.width = 1
  noscriptImg.style.display = 'none'
  noscriptImg.src = `https://www.facebook.com/tr?id=${PIXEL_ID}&ev=PageView&noscript=1`
  document.body.appendChild(noscriptImg)
}

/**
 * Fire a tracking pixel via image request (reliable fallback).
 */
function sendPixelViaImage(event: string, params?: Record<string, any>) {
  const url = new URL('https://www.facebook.com/tr')
  url.searchParams.set('id', PIXEL_ID)
  url.searchParams.set('ev', event)
  url.searchParams.set('noscript', '1')
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      url.searchParams.set(`cd[${k}]`, String(v))
    })
  }

  // Method A: Image pixel
  const img = new Image(1, 1)
  img.src = url.toString()

  // Method B: sendBeacon (more reliable, survives page unload)
  if (navigator.sendBeacon) {
    navigator.sendBeacon(url.toString())
  }
}

/**
 * Track a Meta Pixel event using all available methods.
 */
export function trackEvent(event: string, data?: Record<string, any>) {
  if (typeof window === 'undefined') return

  // Ensure pixel is initialized
  if (!pixelInitialized) {
    initMetaPixel()
  }

  // Method 1: fbq (standard)
  try {
    if (window.fbq) {
      window.fbq('track', event, data)
    }
  } catch (e) {
    // silently fail
  }

  // Method 2: Image pixel + sendBeacon (fallback)
  sendPixelViaImage(event, data)

  // Debug log
  console.log(`[Meta Pixel] ✅ ${event}`, data || '')
}

/**
 * React hook: initializes pixel and fires PageView on mount.
 */
export function useMetaPixel() {
  useEffect(() => {
    initMetaPixel()

    // Small delay to ensure script has started loading
    setTimeout(() => {
      trackEvent('PageView')
    }, 100)
  }, [])
}
